import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight, BarChart3, CheckCircle2, ClipboardList, MapPin, QrCode, Sparkles, Store, Ticket, } from 'lucide-react';
import { HowItWorks } from '@/routes/_layout/-components/how-it-works';
export const Route = createFileRoute('/_layout/learn/offline-marketing-analytics/')({
    component: LearnOfflineMarketingAnalyticsPage,
    head: () => ({
        meta: [
            { title: 'Offline Marketing Analytics — Track Flyers, QR Codes & Campaigns | Adora' },
            {
                name: 'description',
                content: 'Learn how to measure offline marketing materials — flyers, menus, packaging, signage — using QR codes and Smart Links as measurable entry points.',
            },
            {
                property: 'og:title',
                content: 'Offline Marketing Analytics — Track Flyers, QR Codes & Campaigns | Adora',
            },
            {
                property: 'og:description',
                content: 'Learn how to measure offline marketing materials — flyers, menus, packaging, signage — using QR codes and Smart Links as measurable entry points.',
            },
        ],
    }),
});
function LearnOfflineMarketingAnalyticsPage() {
    return (<main className="mx-auto max-w-6xl px-6 md:px-8 py-12">
            {/* HERO */}
            <section className="relative overflow-hidden rounded-2xl border bg-muted/20 p-6 md:p-10">
                <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl"/>
                <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl"/>

                <div className="relative grid gap-8 lg:grid-cols-[1.25fr_0.75fr] lg:items-start">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full border bg-background/60 px-3 py-1 text-xs text-muted-foreground">
                            <Sparkles className="h-3.5 w-3.5"/>
                            Learn • Offline marketing analytics
                        </div>

                        <h1 className="mt-4 text-3xl md:text-5xl font-bold leading-tight tracking-tight">
                            Offline Marketing Analytics
                        </h1>

                        <p className="mt-3 max-w-2xl text-sm md:text-base text-muted-foreground">
                            Measure how <strong>flyers</strong>, <strong>menus</strong>, <strong>packaging</strong> and{' '}
                            <strong>in-store signage</strong> perform — using QR codes and short links as measurable
                            entry points.
                        </p>

                        <p className="mt-2 max-w-2xl text-sm md:text-base text-muted-foreground">
                            With{' '}
                            <Link to="/what-is-adora" className="text-primary hover:underline">
                                Adora Analytics
                            </Link>
                            , offline materials become measurable campaigns — not guesses.
                        </p>

                        <div className="mt-6 flex flex-col sm:flex-row gap-2">
                            <Button asChild size="lg">
                                <Link to="/sign-up" search={{ redirect: '/console' }}>
                                    Start free <ArrowRight className="ml-2 h-4 w-4"/>
                                </Link>
                            </Button>

                            <Button asChild variant="outline" size="lg">
                                <Link to="/how-it-works">See how it works</Link>
                            </Button>

                            <Button asChild variant="ghost" size="lg">
                                <Link to="/compare">Compare tools</Link>
                            </Button>
                        </div>

                        {/* quick signals */}
                        <div className="mt-6 flex flex-wrap gap-2">
                            {[
            'Track placements',
            'Compare flyers',
            'Measure menus',
            'Understand scans vs uniques',
            'Improve what works',
        ].map((x) => (<span key={x} className="inline-flex items-center rounded-full border bg-background/60 px-3 py-1 text-xs text-muted-foreground">
                                    <CheckCircle2 className="mr-1.5 h-3.5 w-3.5"/>
                                    {x}
                                </span>))}
                        </div>
                    </div>

                    {/* right side cards */}
                    <div className="grid gap-3">
                        <MiniStat icon={<QrCode className="h-4 w-4"/>} title="Entry points" desc="QR codes + Smart Links that represent real-world context."/>
                        <MiniStat icon={<BarChart3 className="h-4 w-4"/>} title="Signals" desc="Scans, uniques, destinations, placement performance."/>
                        <MiniStat icon={<ClipboardList className="h-4 w-4"/>} title="Decisions" desc="Repeat what works, remove what doesn’t — with evidence."/>
                    </div>
                </div>
            </section>

            <Separator className="my-12"/>

            {/* PROBLEM (DESIGNED) */}
            <section className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
                <div>
                    <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">The offline marketing problem</h2>
                    <p className="mt-2 text-sm md:text-base text-muted-foreground max-w-xl">
                        Businesses spend money on print every day — but most don’t know which materials actually drive
                        action.
                    </p>

                    <div className="mt-5 grid gap-3">
                        <ProblemItem icon={<MapPin className="h-4 w-4"/>} text="Which placement drives the most engagement?"/>
                        <ProblemItem icon={<Ticket className="h-4 w-4"/>} text="Does this flyer work better than the last one?"/>
                        <ProblemItem icon={<Store className="h-4 w-4"/>} text="Is performance driven by location, design, or message?"/>
                    </div>
                </div>

                <Card className="overflow-hidden">
                    <CardHeader>
                        <CardTitle className="text-lg">What you need (not more links)</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid gap-3 sm:grid-cols-2">
                            <NeedItem title="Context" desc="Where did the scan come from? (menu vs flyer vs counter)"/>
                            <NeedItem title="Comparison" desc="Which variant/placement performed best?"/>
                            <NeedItem title="Consistency" desc="Keep one campaign while testing contexts."/>
                            <NeedItem title="Clarity" desc="Simple signals you can act on immediately."/>
                        </div>

                        <div className="rounded-xl border bg-muted/30 p-4 text-sm text-muted-foreground">
                            Without offline analytics, decisions become intuition. With offline analytics, decisions
                            become repeatable.
                        </div>
                    </CardContent>
                </Card>
            </section>

            <Separator className="my-12"/>

            {/* WHAT IT MEANS (STEP CARDS) */}
            <section>
                <div className="flex flex-col gap-2">
                    <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">
                        What offline marketing analytics actually means
                    </h2>
                    <p className="text-sm md:text-base text-muted-foreground max-w-3xl">
                        Offline marketing analytics connects physical entry points to measurable actions and gives you a
                        way to compare placements like you’d compare ads.
                    </p>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-4">
                    <StepCard step="1" title="Campaign" desc="Create one campaign per promotion, label, flyer or experiment." icon={<TargetIcon />}/>
                    <StepCard step="2" title="Entry points" desc="Generate QR codes + Smart Links for each context." icon={<QrCode className="h-5 w-5"/>}/>
                    <StepCard step="3" title="Distribution" desc="Put them on menus, packaging, signs, posters, social posts." icon={<MapPin className="h-5 w-5"/>}/>
                    <StepCard step="4" title="Insight" desc="See what worked, compare placements, and iterate." icon={<BarChart3 className="h-5 w-5"/>}/>
                </div>
            </section>

            <Separator className="my-12"/>

            {/* HOW IT WORKS (REUSED COMPONENT, but wrapped) */}
            <section className="rounded-2xl border bg-muted/10 p-6 md:p-8">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h2 className="text-xl md:text-2xl font-semibold tracking-tight">
                            How offline tracking works with Adora
                        </h2>
                        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                            This is the operational flow — from campaign creation to measurable outcomes.
                        </p>
                    </div>
                    <Button asChild variant="outline" className="hidden md:inline-flex">
                        <Link to="/how-it-works">Open How it works</Link>
                    </Button>
                </div>

                <div className="mt-6">
                    <HowItWorks />
                </div>
            </section>

            <Separator className="my-12"/>

            {/* SMART LINKS (DESIGNED WRAPPER) */}
            <section className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-start">
                <div>
                    <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Why Smart Links matter</h2>
                    <p className="mt-2 text-sm md:text-base text-muted-foreground max-w-xl">
                        Offline campaigns often reuse the same destination (menu, Telegram, WhatsApp, Instagram). The
                        real question is: <strong>where did people come from?</strong>
                    </p>

                    <div className="mt-5 grid gap-3">
                        <BenefitItem title="Reuse the same campaign" desc="Same campaign, multiple contexts."/>
                        <BenefitItem title="Track each placement" desc="Menu vs flyer vs counter vs packaging."/>
                        <BenefitItem title="Compare results" desc="Know which context performs best."/>
                    </div>
                </div>

                <Card className="my-10">
                    <CardContent className="p-6 items-center">
                        <div>
                            <h3 className="text-xl font-semibold">Smart Links connect context to results</h3>
                            <p className="text-sm text-muted-foreground mt-2">
                                Offline campaigns often reuse the same destination. Smart Links let you track
                                <strong> where people came from</strong> without changing your QR code.
                            </p>
                        </div>

                        <div className="flex flex-col mt-6 gap-2">
                            <Link to="/learn/smart-links" className="text-primary font-medium hover:underline">
                                Learn how Smart Links work →
                            </Link>
                            <Link to="/compare/linktree" className="text-muted-foreground hover:text-foreground hover:underline text-sm">
                                Compare Adora vs Linktree →
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </section>

            <Separator className="my-12"/>

            {/* OFFLINE VS ONLINE (BETTER DESIGN) */}
            <section>
                <div className="flex flex-col gap-2">
                    <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Offline vs online analytics</h2>
                    <p className="text-sm md:text-base text-muted-foreground max-w-3xl">
                        Online analytics assumes digital discovery. Offline analytics measures discovery through the
                        physical world.
                    </p>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-2">
                    <CompareCard title="Online analytics" items={[
            'Tracks ads, websites, social clicks',
            'Assumes digital-first discovery',
            'Weak visibility into physical materials',
            'Hard to compare print placements',
        ]}/>
                    <CompareCard title="Offline analytics (Adora)" highlight items={[
            'Tracks QR codes and Smart Links',
            'Built for real-world distribution',
            'Compares placements and contexts',
            'Turns print into measurable campaigns',
        ]}/>
                </div>
            </section>

            <Separator className="my-12"/>

            {/* WHO IT IS FOR (TILES) */}
            <section>
                <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Who it’s for</h2>
                <p className="mt-2 text-sm md:text-base text-muted-foreground max-w-3xl">
                    If you distribute anything in the real world, you can measure it.
                </p>

                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <AudienceTile icon={<Store className="h-5 w-5"/>} title="Restaurants & cafés" desc="Menus, table tents, counter QR codes."/>
                    <AudienceTile icon={<Ticket className="h-5 w-5"/>} title="Retail stores" desc="In-store signage and promos."/>
                    <AudienceTile icon={<QrCode className="h-5 w-5"/>} title="Packaged products" desc="Labels, packaging, inserts."/>
                    <AudienceTile icon={<ClipboardList className="h-5 w-5"/>} title="Printers & agencies" desc="Report performance to clients."/>
                    <AudienceTile icon={<MapPin className="h-5 w-5"/>} title="Events" desc="Booths, posters, activations."/>
                    <AudienceTile icon={<BarChart3 className="h-5 w-5"/>} title="Street campaigns" desc="Flyers, posters, distribution."/>
                </div>
            </section>

            <Separator className="my-12"/>

            {/* CTA (STRONG) */}
            <section className="rounded-2xl border bg-muted/20 p-6 md:p-10">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div>
                        <h3 className="text-2xl md:text-3xl font-semibold tracking-tight">
                            Start measuring offline campaigns
                        </h3>
                        <p className="mt-2 text-sm md:text-base text-muted-foreground max-w-2xl">
                            Create campaigns for menus, flyers, packaging and placements — and finally see what works.
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                        <Button asChild size="lg">
                            <Link to="/sign-up" search={{ redirect: '/console' }}>
                                Create free account <ArrowRight className="ml-2 h-4 w-4"/>
                            </Link>
                        </Button>
                        <Button asChild size="lg" variant="outline">
                            <Link to="/pricing">View pricing</Link>
                        </Button>
                    </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-2 rounded-full border bg-background/60 px-3 py-1">
                        <CheckCircle2 className="h-3.5 w-3.5"/> Unlimited scans & clicks
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full border bg-background/60 px-3 py-1">
                        <CheckCircle2 className="h-3.5 w-3.5"/> Campaign-based tracking
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full border bg-background/60 px-3 py-1">
                        <CheckCircle2 className="h-3.5 w-3.5"/> Built for offline distribution
                    </span>
                </div>
            </section>
        </main>);
}
function MiniStat({ icon, title, desc }) {
    return (<Card className="bg-background/60">
            <CardContent className="p-4">
                <div className="flex items-start gap-3">
                    <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                        {icon}
                    </div>
                    <div>
                        <div className="font-medium">{title}</div>
                        <div className="text-sm text-muted-foreground">{desc}</div>
                    </div>
                </div>
            </CardContent>
        </Card>);
}
function ProblemItem({ icon, text }) {
    return (<div className="flex items-start gap-3 rounded-xl border bg-muted/10 p-4">
            <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                {icon}
            </div>
            <div className="text-sm text-muted-foreground">{text}</div>
        </div>);
}
function NeedItem({ title, desc }) {
    return (<div className="rounded-xl border bg-background/60 p-4">
            <div className="font-medium">{title}</div>
            <div className="mt-1 text-sm text-muted-foreground">{desc}</div>
        </div>);
}
function StepCard({ step, title, desc, icon }) {
    return (<Card className="h-full">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <div className="inline-flex items-center gap-2">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                            {step}
                        </span>
                        <CardTitle className="text-base">{title}</CardTitle>
                    </div>
                    <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-muted text-foreground">
                        {icon}
                    </div>
                </div>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">{desc}</CardContent>
        </Card>);
}
function BenefitItem({ title, desc }) {
    return (<div className="flex items-start gap-3 rounded-xl border bg-muted/10 p-4">
            <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary"/>
            <div>
                <div className="font-medium">{title}</div>
                <div className="text-sm text-muted-foreground">{desc}</div>
            </div>
        </div>);
}
function CompareCard({ title, items, highlight }) {
    return (<Card className={highlight ? 'border-primary/40 bg-primary/5' : undefined}>
            <CardHeader>
                <CardTitle className="text-lg">{title}</CardTitle>
            </CardHeader>
            <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                    {items.map((x) => (<li key={x} className="flex items-start gap-2">
                            <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-foreground">
                                <CheckCircle2 className="h-3.5 w-3.5"/>
                            </span>
                            <span>{x}</span>
                        </li>))}
                </ul>
            </CardContent>
        </Card>);
}
function AudienceTile({ icon, title, desc }) {
    return (<Card className="h-full">
            <CardContent className="p-5">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                    {icon}
                </div>
                <div className="mt-3 font-medium">{title}</div>
                <div className="mt-1 text-sm text-muted-foreground">{desc}</div>
            </CardContent>
        </Card>);
}
function TargetIcon() {
    return <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border">◎</span>;
}
